import { IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Icon } from '@sdb/planning-ui';

import { RegisterAbsenceProposalEntry } from 'RestAPIs/roster/register-absence';

import {
  ContentSection,
  ProposalActions,
  ProposalCode,
  ProposalColumn,
  ProposalColumnHeader,
  ProposalContent,
  ProposalDate,
  ProposalEmptyState,
  ProposalGrid,
  ProposalHours,
  ProposalPeriod,
  ProposalPeriods,
  ProposalRow,
  ProposalScrollArea,
  SectionTitle
} from './LeaveRequestProposalDialog.styles';
import {
  formatDate,
  formatHours,
  formatPeriods,
  getDutyCode,
  getEntryDate
} from './LeaveRequestProposalDialog.utils';

interface SingleDayProposalViewProps {
  proposals: RegisterAbsenceProposalEntry[];
  onRemoveDuty: (entry: RegisterAbsenceProposalEntry) => void;
  onRemoveOpenRequest: (entry: RegisterAbsenceProposalEntry) => void;
  onEditDuty: (index: number) => void;
}

const SingleDayProposalView = ({
  proposals,
  onRemoveDuty,
  onRemoveOpenRequest,
  onEditDuty
}: SingleDayProposalViewProps) => {
  const { t } = useTranslation('common');

  const dutyEntries = proposals.flatMap((entry, index) =>
    entry.dutyLine ? [{ entry, index }] : []
  );
  const openRequestEntries = proposals.flatMap((entry, index) =>
    entry.openRequest ? [{ entry, index }] : []
  );

  return (
    <ContentSection>
      <SectionTitle>
        {t('plannerMessages.leaveRequestDialog.proposalTitle')}
      </SectionTitle>

      <ProposalGrid>
        <ProposalColumn>
          <ProposalColumnHeader>
            {t('plannerMessages.leaveRequestDialog.duties')}
          </ProposalColumnHeader>
          {dutyEntries.length === 0 ? (
            <ProposalEmptyState>
              {t('plannerMessages.leaveRequestDialog.noDuties')}
            </ProposalEmptyState>
          ) : (
            <ProposalScrollArea>
              {dutyEntries.map(({ entry, index }) => (
                <ProposalRow key={`${getEntryDate(entry)}-${index}-duty`}>
                  <ProposalDate>{formatDate(getEntryDate(entry))}</ProposalDate>
                  <ProposalCode>{getDutyCode(entry)}</ProposalCode>
                  <ProposalHours>{formatHours(entry)}</ProposalHours>
                  <ProposalContent>
                    <ProposalPeriods>
                      {formatPeriods(entry).map((period) => (
                        <ProposalPeriod key={period.key}>
                          {period.label}
                          {period.description ? ` ${period.description}` : ''}
                        </ProposalPeriod>
                      ))}
                    </ProposalPeriods>
                  </ProposalContent>
                  <ProposalActions>
                    <IconButton
                      aria-label={t(
                        'plannerMessages.leaveRequestDialog.editDutyPeriods'
                      )}
                      onClick={() => onEditDuty(index)}
                      size="small"
                    >
                      <Icon type="pen" fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={t(
                        'plannerMessages.leaveRequestDialog.removeDuty'
                      )}
                      onClick={() => onRemoveDuty(entry)}
                      size="small"
                    >
                      <Icon type="xmark" fontSize="small" />
                    </IconButton>
                  </ProposalActions>
                </ProposalRow>
              ))}
            </ProposalScrollArea>
          )}
        </ProposalColumn>

        <ProposalColumn>
          <ProposalColumnHeader>
            {t('plannerMessages.leaveRequestDialog.dutiesToReplace')}
          </ProposalColumnHeader>
          {openRequestEntries.length === 0 ? (
            <ProposalEmptyState>
              {t('plannerMessages.leaveRequestDialog.noReplacementDuties')}
            </ProposalEmptyState>
          ) : (
            <ProposalScrollArea>
              {openRequestEntries.map(({ entry, index }) => (
                <ProposalRow
                  key={`${getEntryDate(entry)}-${index}-open-request`}
                >
                  <ProposalDate>{formatDate(getEntryDate(entry))}</ProposalDate>
                  <ProposalCode>{getDutyCode(entry)}</ProposalCode>
                  <ProposalHours>{formatHours(entry)}</ProposalHours>
                  <ProposalContent>
                    <ProposalPeriods>
                      {formatPeriods(entry).map((period) => (
                        <ProposalPeriod key={period.key}>
                          {period.label}
                          {period.description ? ` ${period.description}` : ''}
                        </ProposalPeriod>
                      ))}
                    </ProposalPeriods>
                  </ProposalContent>
                  <ProposalActions>
                    <IconButton
                      aria-label={t(
                        'plannerMessages.leaveRequestDialog.removeReplacementDuty'
                      )}
                      onClick={() => onRemoveOpenRequest(entry)}
                      size="small"
                    >
                      <Icon type="xmark" fontSize="small" />
                    </IconButton>
                  </ProposalActions>
                </ProposalRow>
              ))}
            </ProposalScrollArea>
          )}
        </ProposalColumn>
      </ProposalGrid>
    </ContentSection>
  );
};

export default SingleDayProposalView;
